/* eslint-disable react/no-unused-prop-types */
import { DateTimePicker, SxProps, Theme, useTheme } from "@crayon/design-system-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { Control, Controller, FieldValues, Path, PathValue, useWatch } from "react-hook-form";

interface FormDateTimePickerProps<TField extends FieldValues> {
  bindSchemaFieldName: Path<TField>;
  control: Control<TField>;
  disabled?: boolean;
  id: string;
  label: string;
  sxRoot?: SxProps<Theme>;
  testId?: string;
  minDate?: Date;
  required?: boolean;
}

const FormDateTimePicker = <TField extends FieldValues>({
  bindSchemaFieldName,
  control,
  id,
  label,
  disabled = false,
  sxRoot = undefined,
  testId = undefined,
  minDate = undefined,
  required = undefined,
}: FormDateTimePickerProps<TField>) => {
  const theme = useTheme();

  const [open, setOpen] = useState(false);

  const initFieldValue = useWatch({ control, name: bindSchemaFieldName });
  const fieldValueToDateTime = (fieldValue: PathValue<TField, Path<TField>> | null) =>
    fieldValue ? DateTime.fromJSDate(fieldValue) : null;
  const [prevDate, setPrevDate] = useState<DateTime | null>(fieldValueToDateTime(initFieldValue));

  return (
    <Controller
      control={control}
      name={bindSchemaFieldName}
      render={({ field, fieldState }) => {
        const onDateChange = (e: DateTime | null) =>
          field.onChange(
            e ? DateTime.utc(e.year, e.month, e.day, e.hour, e.minute, e.second).toJSDate() : null,
          );

        const saveToPrevValueAndClose = (fieldValue: PathValue<TField, Path<TField>> | null) => {
          setPrevDate(fieldValueToDateTime(fieldValue));
          setOpen(false);
        };

        return (
          <DateTimePicker
            id={id}
            testId={testId}
            required={required}
            label={label}
            disabled={disabled}
            views={["year", "month", "day", "hours", "minutes", "seconds"]}
            minDate={minDate ? DateTime.fromJSDate(minDate) : undefined}
            value={field.value ? DateTime.fromJSDate(field.value, { zone: "utc" }) : null}
            onChange={(e: DateTime | null) => onDateChange(e)}
            closeOnSelect={false}
            sx={{
              ...sxRoot,
              "& input": {
                color: theme.palette.secondary.main,
              },
            }}
            slotProps={{
              field: { clearable: true },
              textField: {
                error: Boolean(fieldState.error),
                helperText: fieldState.error?.message,
              },
              // See comment FromDatePicker.tsx for more info
              actionBar: {
                onCancel() {
                  onDateChange(prevDate);
                  setOpen(false);
                },
                onAccept() {
                  saveToPrevValueAndClose(field.value);
                },
                onClear() {
                  onDateChange(null);
                  saveToPrevValueAndClose(null);
                },
                actions: field.value ? ["clear", "cancel", "accept"] : ["cancel", "accept"],
              },
            }}
            open={open}
            onOpen={() => setOpen(true)}
            // called on clickaway
            onClose={() => saveToPrevValueAndClose(field.value)}
          />
        );
      }}
    />
  );
};

export default FormDateTimePicker;
